import { Link } from "@/lib/router-compat";

export type LocalNote = { title: string; body: React.ReactNode };

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Helvetica, Arial, sans-serif";

/**
 * Unique, town-specific context block for local SEO pages.
 * Each page passes its own copy — no shared boilerplate text.
 */
const LocalContextSection = ({
  heading,
  intro,
  notes,
  gettingHereHeading,
  gettingHere,
  closing,
}: {
  heading: string;
  intro: React.ReactNode;
  notes: LocalNote[];
  gettingHereHeading: string;
  gettingHere: React.ReactNode[];
  closing?: React.ReactNode;
}) => (
  <section style={{ background: "#FFFFFF", padding: "60px 20px" }}>
    <div className="mx-auto" style={{ maxWidth: 1100 }}>
      <h2
        style={{
          fontFamily: SERIF,
          color: "#8B1A1A",
          fontSize: 30,
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        {heading}
      </h2>
      <p
        style={{
          fontFamily: SANS,
          color: "#2C2C2C",
          fontSize: 16,
          lineHeight: 1.7,
          marginTop: 16,
          marginBottom: 28,
        }}
      >
        {intro}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 18,
        }}
      >
        {notes.map((n) => (
          <div
            key={n.title}
            style={{
              background: "#FAF4F0",
              border: "1px solid #F0E0E0",
              borderRadius: 12,
              padding: 20,
            }}
          >
            <h3
              style={{
                fontFamily: SANS,
                color: "#6B1414",
                fontSize: 17,
                fontWeight: 700,
                margin: 0,
                marginBottom: 8,
              }}
            >
              {n.title}
            </h3>
            <p
              style={{
                fontFamily: SANS,
                color: "#2C2C2C",
                fontSize: 15,
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              {n.body}
            </p>
          </div>
        ))}
      </div>

      <h3
        style={{
          fontFamily: SERIF,
          color: "#8B1A1A",
          fontSize: 22,
          marginTop: 36,
          marginBottom: 12,
        }}
      >
        {gettingHereHeading}
      </h3>
      <ul
        style={{
          fontFamily: SANS,
          color: "#2C2C2C",
          fontSize: 15,
          lineHeight: 1.8,
          paddingLeft: 22,
          margin: 0,
        }}
      >
        {gettingHere.map((g, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {g}
          </li>
        ))}
      </ul>

      {closing ? (
        <p
          style={{
            fontFamily: SANS,
            color: "#2C2C2C",
            fontSize: 16,
            lineHeight: 1.7,
            marginTop: 24,
            marginBottom: 0,
          }}
        >
          {closing}
        </p>
      ) : null}
    </div>
  </section>
);

export const LocalLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} style={{ color: "#8B1A1A", textDecoration: "underline", fontWeight: 600 }}>
    {children}
  </Link>
);

export default LocalContextSection;
